import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import type { Entry } from '../lib/api';
import { Button, WordCountChart, WordCountAverages, OnThisDayMap, OnThisDayList } from '../components/ui';

interface DayStat {
  date: string;
  wordCount: number;
}

function fillDays(raw: DayStat[], days: number): DayStat[] {
  const byDate = new Map(raw.map((s) => [s.date, s.wordCount]));
  const filled: DayStat[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    filled.push({ date: dateStr, wordCount: byDate.get(dateStr) ?? 0 });
  }
  return filled;
}

export function TodayPage() {
  const { t, i18n } = useTranslation();
  const [todayEntry, setTodayEntry] = useState<Entry | null | undefined>(undefined);
  const [stats, setStats] = useState<DayStat[] | null>(null);
  const [onThisDay, setOnThisDay] = useState<Entry[] | null>(null);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];

  const formattedDate = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(today);

  useEffect(() => {
    const [year, month, day] = todayStr.split('-').map(Number);
    const startDate = new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
    const endDate = new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();

    api
      .listEntries({ startDate, endDate, limit: '1' })
      .then((data) => setTodayEntry(data.items[0] ?? null))
      .catch(() => setTodayEntry(null));

    api
      .getWordCountStats(365)
      .then(({ stats: raw }) => setStats(fillDays(raw, 365)))
      .catch(() => setStats([]));

    api
      .getEntriesOnThisDay()
      .then(({ entries }) => setOnThisDay(entries))
      .catch(() => setOnThisDay([]));
  }, [todayStr]);

  if (todayEntry === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl flex flex-col gap-6 py-8 px-4">
      <div>
        <h1 className="text-xl font-semibold text-ink">{t('today.title')}</h1>
        <p className="mt-1 text-sm text-ink-400 capitalize">{formattedDate}</p>
      </div>

      {todayEntry ? (
        <div className="card flex flex-col gap-3">
          <p className="text-sm text-ink-500">{t('today.entryExists')}</p>
          <Link
            to={`/entries/${todayEntry.id}/view`}
            className="link font-medium"
          >
            {todayEntry.title || t('today.untitled')}
          </Link>
        </div>
      ) : (
        <div className="card flex flex-col gap-3">
          <p className="text-sm text-ink-500">{t('today.noEntry')}</p>
          <div>
            <Link to="/entries/new">
              <Button variant="primary" size="sm">{t('entries.newEntry')}</Button>
            </Link>
          </div>
        </div>
      )}

      {stats && stats.length > 0 && (
        <>
          <div className="card flex flex-col gap-3">
            <p className="text-sm font-medium text-ink">{t('today.last30Days')}</p>
            <WordCountChart data={stats.slice(-30)} />
          </div>

          <div className="card">
            <p className="text-sm font-medium text-ink mb-3">{t('today.averageWordsPerDay')}</p>
            <WordCountAverages data={stats} />
          </div>
        </>
      )}

      {onThisDay && onThisDay.length > 0 && (
        <>
          <div className="card flex flex-col gap-3">
            <p className="text-sm font-medium text-ink">{t('today.onThisDay')}</p>
            <OnThisDayMap entries={onThisDay} />
          </div>

          <div className="card flex flex-col gap-1">
            <p className="text-sm font-medium text-ink mb-1">{t('today.onThisDay')}</p>
            <OnThisDayList entries={onThisDay} />
          </div>
        </>
      )}
    </div>
  );
}
